import { useState } from "react"
import { RotateCcw } from "lucide-react"

import { Textarea } from "@/components/ui/textarea"
import { Shell, Note, Field, Footer } from "./modalChrome"

// Request-changes dialog for every order type; mounted by OrderModals under
// the "request-revision" key. In its own file because it carries the most
// form logic of the set: the overall note plus, on illustration orders, one
// optional comment per spread (AU-29).
export default function RequestRevisionDialog({ order, runAction, onClose }) {
  const { max, used = 0 } = order.revisions ?? {}
  const remaining = max != null ? Math.max(max - used, 0) : null
  // A draft round (the order is in Draft Approval) is the writer reworking the
  // draft before it goes any further, not a revision of delivered work; the
  // dialog says so and names the round the way the Revision Requests tab will
  // (UPDA-104).
  const isDraft = order.phase === "draft"
  const [notes, setNotes] = useState("")
  // Per-spread comments (AU-29), illustration orders only. Keyed by the
  // spread's blobId from the presenter payload — the identity that survives
  // archival, so each comment lands under the exact image in the illustrator's
  // Revision History. Spreads without one send nothing.
  const spreads =
    order.type === "illustration"
      ? (order.deliverables?.illustrations ?? []).filter((s) => s.blobId != null)
      : []
  const [imageComments, setImageComments] = useState({})
  const setImageComment = (blobId, text) =>
    setImageComments((prev) => ({ ...prev, [blobId]: text }))
  const submit = () => {
    const comments = spreads
      .map((s) => ({ blobId: s.blobId, comment: (imageComments[s.blobId] ?? "").trim() }))
      .filter((c) => c.comment)
    runAction("review", {
      approved: false,
      notes,
      ...(comments.length ? { imageComments: comments } : {}),
    })
  }

  return (
    <Shell title={isDraft ? "Request changes to the draft" : "Request a revision"} onClose={onClose}>
      <Note tone="info">
        {isDraft ? (
          <>
            Your writer will revise the draft and send it back for another look.
            {remaining != null && (
              <>
                {" "}This uses one of your <b className="font-bold text-primary">{remaining} remaining revision{remaining === 1 ? "" : "s"}</b>.
              </>
            )}
          </>
        ) : remaining != null ? (
          <>
            You have <b className="font-bold text-primary">{remaining} revision{remaining === 1 ? "" : "s"}</b> remaining on this order.
          </>
        ) : (
          "Describe the changes you'd like your team to make."
        )}
      </Note>
      <Field label={isDraft ? "What should change in the draft?" : "What would you like revised?"} htmlFor="revision-notes">
        <Textarea
          id="revision-notes"
          rows={6}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={
            spreads.length
              ? "Overall notes for the whole set — you can also comment on individual illustrations below."
              : isDraft
                ? "Name the chapters or sections and what should be different — your writer reads exactly what you write here."
                : "Be specific — reference chapters, pages or doc comments so your team knows exactly what to change."
          }
        />
      </Field>
      {spreads.length > 0 && (
        <Field label="Comments on individual illustrations (optional)">
          <div className="max-h-[300px] space-y-3 overflow-y-auto pr-1">
            {spreads.map((spread, i) => (
              <div key={spread.blobId} className="flex items-start gap-3">
                <img
                  src={spread.url}
                  alt={`Illustration ${i + 1}`}
                  className="mt-1 size-14 shrink-0 rounded-lg border border-border object-cover"
                  loading="lazy"
                />
                <div className="min-w-0 flex-1">
                  <label
                    htmlFor={`image-comment-${spread.blobId}`}
                    className="mb-1 block text-[12px] font-semibold text-muted-foreground"
                  >
                    Illustration {i + 1}
                  </label>
                  <Textarea
                    id={`image-comment-${spread.blobId}`}
                    rows={2}
                    value={imageComments[spread.blobId] ?? ""}
                    onChange={(e) => setImageComment(spread.blobId, e.target.value)}
                    placeholder="What should change in this one?"
                  />
                </div>
              </div>
            ))}
          </div>
        </Field>
      )}
      <Footer SubmitIcon={RotateCcw} submitLabel={isDraft ? "Send change request" : "Send revision request"} onSubmit={submit} />
    </Shell>
  )
}
